import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react'
import {
  DEFAULT_LANGUAGE,
  getAvailableLanguages,
  isSupportedLanguage,
  LANGUAGE_STORAGE_KEY
} from './config'
import { applyGoogleTranslateLanguage } from './googleTranslate'

const LanguageContext = createContext({
  language: DEFAULT_LANGUAGE,
  setLanguage: () => {},
  languages: getAvailableLanguages()
})

const getInitialLanguage = () => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE

  const storedLanguage = window.localStorage.getItem(LANGUAGE_STORAGE_KEY)
  if (storedLanguage && isSupportedLanguage(storedLanguage)) {
    return storedLanguage
  }

  return DEFAULT_LANGUAGE
}

export const LanguageProvider = ({ children }) => {
  const languages = useMemo(() => getAvailableLanguages(), [])
  const [language, setLanguage] = useState(getInitialLanguage)

  useEffect(() => {
    if (typeof window === 'undefined') return

    if (!isSupportedLanguage(language)) {
      setLanguage(DEFAULT_LANGUAGE)
      return
    }

    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language)

    const includedLanguageCodes = languages.map(item => item.code)
    applyGoogleTranslateLanguage({
      languageCode: language,
      includedLanguages: includedLanguageCodes
    }).catch(error => {
      // Keep UI responsive even when translation script is blocked.
      // eslint-disable-next-line no-console
      console.warn('Language translation failed:', error)
    })
  }, [language, languages])

  const value = useMemo(
    () => ({
      language,
      setLanguage,
      languages
    }),
    [language, languages]
  )

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  )
}

export const useLanguage = () => useContext(LanguageContext)
